/**
 * Global search — autofocused input in the top bar, debounced query,
 * grouped results (Notices / Events / Vendors / Residents).
 * Wired: GET /api/account/search?q=. Port of screens-account.jsx SearchScreen.
 */
import { router } from 'expo-router';
import React from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { space } from '@bero/tokens';
import { str } from '@/components/gate/api';
import { goBack, IconCircle } from '@/features/account-admin/bits';
import { Icon, type IconName } from '@/features/account-admin/icons';
import { useT } from '@/i18n';
import type { DictKey } from '@/i18n/en';
import { api } from '@/lib/client';
import { useTheme } from '@/theme';
import { AppText, EmptyState, IconButton, Input, Screen, SectionLabel, Skeleton } from '@/ui';

type Hit = { id: string; label: string; sub?: string };
type Group = { key: string; titleKey: DictKey; icon: IconName; hits: Hit[] };

const GROUPS: Array<[key: string, titleKey: DictKey, icon: IconName]> = [
  ['notices', 'ct_tab_notices', 'megaphone'],
  ['events', 'ct_tab_events', 'calendar'],
  ['vendors', 'ml_vendors', 'wrench'],
  ['residents', 'row_residents', 'user'],
];

function toHit(raw: unknown, i: number): Hit {
  return {
    id: str(raw, 'id') ?? `h${i}`,
    label: str(raw, 'title', 'name', 'label', 'fullName') ?? '',
    sub: str(raw, 'subtitle', 'flat', 'flatCode', 'when', 'category'),
  };
}

function parseGroups(body: unknown): Group[] {
  const src = (body && typeof body === 'object'
    ? ((body as Record<string, unknown>).results ?? body)
    : {}) as Record<string, unknown>;
  return GROUPS.map(([key, titleKey, icon]) => {
    const raw = src[key];
    const hits = (Array.isArray(raw) ? raw : []).map(toHit).filter((h) => h.label);
    return { key, titleKey, icon, hits };
  }).filter((g) => g.hits.length > 0);
}

function openHit(groupKey: string, hit: Hit) {
  if (groupKey === 'residents') router.push('/residents');
  else router.push(`/${groupKey}/${hit.id}`);
}

export default function SearchScreen() {
  const { colors } = useTheme();
  const { t } = useT();
  const insets = useSafeAreaInsets();
  const [q, setQ] = React.useState('');
  const [groups, setGroups] = React.useState<Group[]>([]);
  const [asyncState, setState] = React.useState<'idle' | 'loading' | 'ready' | 'error'>('idle');
  const seq = React.useRef(0);
  const state = q.trim().length < 2 ? 'idle' : asyncState;

  React.useEffect(() => {
    const query = q.trim();
    const id = ++seq.current;
    if (query.length < 2) {
      return;
    }
    void Promise.resolve().then(() => {
      if (id !== seq.current) return;
      setState('loading');
    });
    const timer = setTimeout(async () => {
      try {
        const body = await api.get<unknown>(`/api/account/search?q=${encodeURIComponent(query)}`);
        if (id !== seq.current) return;
        setGroups(parseGroups(body));
        setState('ready');
      } catch {
        if (id !== seq.current) return;
        setGroups([]);
        setState('error');
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [q]);

  return (
    <Screen scroll={false}>
      <View style={{
        paddingTop: insets.top, backgroundColor: colors.canvas0,
        borderBottomWidth: StyleSheet.hairlineWidth, borderBottomColor: colors.borderSoft,
      }}>
        <View style={{ height: 56, flexDirection: 'row', alignItems: 'center', gap: space.s2, paddingHorizontal: space.s2 }}>
          <IconButton label={t('sr_back')} onPress={goBack}>
            <Icon name="chevron-left" size={24} color={colors.textPrimary} />
          </IconButton>
          <Input
            autoFocus
            value={q}
            onChangeText={setQ}
            placeholder={t('sr_placeholder')}
            returnKeyType="search"
            style={{ flex: 1, marginRight: space.s2 }}
          />
        </View>
      </View>
      <Screen padded style={{ paddingTop: space.s4 }}>
        {state === 'idle' ? (
          <EmptyState
            icon={<Icon name="search" size={40} color={colors.textTertiary} />}
            title={t('sr_idle_title')}
            body={t('sr_idle_body')}
          />
        ) : state === 'loading' ? (
          <View style={{ gap: space.s3 }}>
            {[0, 1, 2, 3, 4].map((i) => <Skeleton key={i} h={48} />)}
          </View>
        ) : state === 'error' ? (
          <EmptyState
            icon={<Icon name="search" size={40} color={colors.textTertiary} />}
            title={t('sr_err_title')}
            body={t('sr_err_body')}
          />
        ) : groups.length === 0 ? (
          <EmptyState
            icon={<Icon name="search" size={40} color={colors.textTertiary} />}
            title={t('res_no_matches')}
            body={t('sr_no_matches_body', { q: q.trim() })}
          />
        ) : (
          groups.map((g) => (
            <View key={g.key} style={{ marginBottom: space.s5 }}>
              <SectionLabel>{t(g.titleKey)}</SectionLabel>
              <View style={{ gap: space.s1 }}>
                {g.hits.map((h) => (
                  <Pressable
                    key={h.id}
                    accessibilityRole="button"
                    onPress={() => openHit(g.key, h)}
                    style={({ pressed }) => ({
                      flexDirection: 'row', alignItems: 'center', gap: space.s3,
                      paddingVertical: 10, backgroundColor: pressed ? colors.canvas1 : 'transparent',
                    })}
                  >
                    <IconCircle name={g.icon} size={36} />
                    <View style={{ flex: 1, minWidth: 0 }}>
                      <AppText variant="bodyMd">{[h.label, h.sub].filter(Boolean).join(' · ')}</AppText>
                    </View>
                    <Icon name="chevron-right" size={16} color={colors.textTertiary} />
                  </Pressable>
                ))}
              </View>
            </View>
          ))
        )}
      </Screen>
    </Screen>
  );
}
